import { useState, useEffect } from "react";
import moment from "moment";
import { Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import Alert from "components/molecules/Alert/Alert";
import TimeInput from "components/atoms/TimeInput/TimeInput";
import FormHr from "pages/shipper/RequestForm/components/Common/FormHr";
import PickupDeliveryDate from "pages/shipper/RequestForm/components/Schedule/PickupDeliveryDate";
import { IQuotationData } from "pages/provider/QuotationDetail/QuotationType";
import { useGetQuotationFormConfig } from "api/queries/quotation-form/useGetQuotationFormConfig";
import { ICountries } from "pages/shipper/RequestForm/components/Schedule/Types";
import OceanFreightDelivery from "./OceanFreightDelivery";
import AirFreightDelivery from "./AirFreightDelivery";
import AirCharterDelivery from "./components/AirCharterDelivery";

interface IDeliveryProps {
  quoteRequestDetails: IQuotationData;
  countries: ICountries[];
}

function Delivery(props: IDeliveryProps) {
  const { quoteRequestDetails, countries } = props;

  const [holidays, setHolidays] = useState({});
  const id = useParams().id?.toUpperCase();
  const { data: quotationConfigData } = useGetQuotationFormConfig(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const intl = useIntl();
  const methods = useFormContext();
  const { getValues, trigger, watch } = methods;
  const selectedFreightModeData = quotationConfigData?.freight_modes?.find(
    (item: any) => item.value === getValues("service.freight_mode"),
  );

  const { stop1_date, stop1_time, stop1_datetime, stop1_end_date, stop1_end_time, stop1_city, stop1_country_code } =
    quoteRequestDetails?.schedule || {};
  const [delivery_time, delivery_date, pickup_time, pickup_date] = watch([
    "schedule.delivery_time",
    "schedule.delivery_date",
    "schedule.pickup_time",
    "schedule.pickup_date",
  ]);

  useEffect(() => {
    trigger("schedule.delivery_date");
    trigger("schedule.pickup_date");
    trigger("schedule.delivery_time");
    trigger("schedule.pickup_time");
  }, [delivery_time, pickup_time, delivery_date, pickup_date]);

  const isOceanFreight =
    ["ocean_freight"].includes(getValues("service.freight_mode")) || selectedFreightModeData?.type === "ocean";
  const isAirCharter = ["air_charter"].includes(getValues("service.freight_mode"));
  const isAirFreight =
    ["obc", "nfo", "regular_air"].includes(getValues("service.freight_mode")) ||
    (selectedFreightModeData?.type === "air" && !isAirCharter);

  const getFormattedDateTime = (key: string) => {
    const dateKey = `${key}_date`;
    const timeKey = `${key}_time`;

    return moment(`${getValues(dateKey)} ${getValues(timeKey)}`, "YYYY-MM-DD HH:mm:ss");
  };

  const isValidDeliveryDate = () =>
    moment(getFormattedDateTime("schedule.delivery")).isAfter(getFormattedDateTime("schedule.pickup"));

  return (
    <>
      <div className="flex-row">
        <div className="w-200 pl-16 pr-16">
          <Row className="mb-1">
            <h6>
              <FormattedMessage id="general.delivery" defaultMessage="Delivery" />
            </h6>
          </Row>
          <Row className="font-b3 text-secondary">
            {stop1_datetime !== "ASAP" &&
              (stop1_end_date && stop1_end_time ? (
                <p className="font-b2">
                  Between{" "}
                  {`${moment(stop1_date).format("MMMM DD, YYYY")} - ${moment(stop1_time, "HH:mm:ss").format("HH:mm")} to ${
                    stop1_end_date !== stop1_date ? `${moment(stop1_end_date).format("MMMM DD, YYYY")} -` : ""
                  } ${moment(stop1_end_time, "HH:mm:ss").format("HH:mm")}`}
                </p>
              ) : (
                <p className="font-b2">
                  {`${moment(stop1_date).format("MMMM DD, YYYY")} - ${moment(stop1_time, "HH:mm:ss").format("HH:mm")}`}
                </p>
              ))}
          </Row>
          <Row className="font-b3 text-secondary">
            <p className="font-b2">{`${stop1_city}, ${stop1_country_code}`}</p>
          </Row>
        </div>
        <div className="flex-row mt-2">
          <div>
            {isOceanFreight && <OceanFreightDelivery countries={countries} />}
            {isAirFreight && <AirFreightDelivery countries={countries} />}
            {isAirCharter && <AirCharterDelivery />}
            <div className="flex-row mb-4 mt-4">
              <div className="w-192 align-date">
                <label className="required">
                  <FormattedMessage id="quote.provider.estimatedDeliveryTime" defaultMessage="Estimated delivery time" />
                </label>
                <PickupDeliveryDate
                  datePickerProps={{
                    name: "schedule.delivery_date",
                    validations: {
                      isValidDeliveryDate: () =>
                        isValidDeliveryDate() ||
                        intl.formatMessage({
                          id: "quote.provider.schedule.latestPickupTimeBeforeEstimatedDelivery",
                          defaultMessage: "Estimated delivery date must be after latest pickup date",
                        }),
                    },
                    required: true,
                  }}
                  countries={countries}
                  holidays={holidays}
                  setHolidays={setHolidays}
                />
              </div>
              <div className="mt-2">
                <label />
                <TimeInput name="schedule.delivery_time" required defaultValue="09:00:00" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-row">
        <div className="w-200 pl-16 pr-16" />
        {!isValidDeliveryDate() && getValues("schedule.delivery_date") && (
          <Alert variant="danger">
            <FormattedMessage
              id="quote.provider.schedule.deliveryDateShouldBeGreaterThanPickupDate"
              defaultMessage="Delivery date-time should be greater than pickup date-time (note: timezone is considered as well)"
            />
          </Alert>
        )}
      </div>
      <FormHr />
    </>
  );
}

export default Delivery;
